import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemButton,
    ListSubheader,
    Chip,
    Box,
    Typography,
    Divider,
} from '@mui/material'
import { useMemo } from 'react'
import { Category, Check } from '@mui/icons-material'
import { type APIProductModel } from '@visorpro/client'
import {
    CREATE_NEW_MODEL,
    IGNORE_MODEL,
    type ProductModelMatching,
} from '../../types'

export interface ModelsDialogProps {
    open: boolean
    modelMatchings: ProductModelMatching[]
    isProcessing: boolean
    onConfirm: () => Promise<void>
    onClose: () => void
    onBack: () => void
    changeModelMatching: (
        modelName: string,
        selected:
            | Fuzzysort.KeyResult<APIProductModel>
            | typeof CREATE_NEW_MODEL
            | typeof IGNORE_MODEL,
    ) => void
}

export const ModelsDialog = ({
    open,
    modelMatchings,
    isProcessing,
    onConfirm,
    onClose,
    onBack,
    changeModelMatching,
}: ModelsDialogProps) => {
    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <DialogTitle>Reuse, Create or Ignore Models</DialogTitle>
            <DialogContent sx={{ px: 0 }}>
                <ModelList
                    modelMatchings={modelMatchings}
                    changeModelMatching={changeModelMatching}
                />
                <Summary modelMatchings={modelMatchings} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button onClick={onBack} disabled={isProcessing}>
                    Back
                </Button>
                <Button
                    color="primary"
                    variant="outlined"
                    onClick={onConfirm}
                    disabled={isProcessing}>
                    Next
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const ModelList = ({
    modelMatchings,
    changeModelMatching,
}: {
    modelMatchings: ProductModelMatching[]
    changeModelMatching: (
        modelName: string,
        selected:
            | Fuzzysort.KeyResult<APIProductModel>
            | typeof CREATE_NEW_MODEL
            | typeof IGNORE_MODEL,
    ) => void
}) => {
    const attentionRequiredModelMatchings = useMemo(() => {
        return modelMatchings.filter(
            (modelMatching) =>
                modelMatching.selected === IGNORE_MODEL ||
                modelMatching.selected === CREATE_NEW_MODEL ||
                modelMatching.results.length < 1 ||
                modelMatching.results[0].score !== 1,
        )
    }, [modelMatchings])

    const perfectlyMatchedModelMatchings = useMemo(() => {
        return modelMatchings.filter(
            (modelMatching) =>
                modelMatching.selected !== IGNORE_MODEL &&
                modelMatching.selected !== CREATE_NEW_MODEL &&
                modelMatching.results.length > 0 &&
                modelMatching.results[0].score === 1,
        )
    }, [modelMatchings])

    return (
        <List sx={{ p: 0, gap: 0, overflowY: 'auto', maxHeight: '70vh' }}>
            <ModelListGroup
                modelMatchings={attentionRequiredModelMatchings}
                changeModelMatching={changeModelMatching}
                groupTitle={`Attention Required (${attentionRequiredModelMatchings.length})`}
            />
            <ModelListGroup
                modelMatchings={perfectlyMatchedModelMatchings}
                changeModelMatching={changeModelMatching}
                groupTitle={`Perfectly Matched (${perfectlyMatchedModelMatchings.length})`}
            />
        </List>
    )
}

const ModelListGroup = ({
    modelMatchings,
    changeModelMatching,
    groupTitle,
}: {
    modelMatchings: ProductModelMatching[]
    changeModelMatching: (
        modelName: string,
        selected:
            | Fuzzysort.KeyResult<APIProductModel>
            | typeof CREATE_NEW_MODEL
            | typeof IGNORE_MODEL,
    ) => void
    groupTitle: string
}) => {
    return (
        <>
            <ListSubheader
                sx={{
                    px: 3,
                    background: (theme) => theme.palette.background.default,
                }}>
                {groupTitle}
            </ListSubheader>
            {modelMatchings.map((modelMatching) => {
                const onCreateClick = () =>
                    changeModelMatching(modelMatching.name, CREATE_NEW_MODEL)
                const onIgnoreClick = () =>
                    changeModelMatching(modelMatching.name, IGNORE_MODEL)

                const hasPerfectMatch =
                    modelMatching.results.length > 0 &&
                    modelMatching.results[0].score === 1
                const results = hasPerfectMatch
                    ? [modelMatching.results[0]]
                    : modelMatching.results

                return (
                    <Box key={modelMatching.name}>
                        <ListItem
                            sx={{
                                py: 1,
                                px: 2,
                                flexDirection: 'column',
                                alignItems: 'start',
                            }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                    mb: 0.2,
                                    pl: '5.9rem',
                                    fontWeight: 600,
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}>
                                {modelMatching.name}
                            </Box>
                            {!hasPerfectMatch && (
                                <ListItemButton
                                    selected={
                                        modelMatching.selected ===
                                        CREATE_NEW_MODEL
                                    }
                                    sx={{ display: 'flex', width: '100%' }}
                                    onClick={onCreateClick}>
                                    <Box
                                        sx={{
                                            width: '4.4rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 0.5,
                                        }}>
                                        <Check
                                            sx={{
                                                opacity:
                                                    modelMatching.selected ===
                                                    CREATE_NEW_MODEL
                                                        ? 1
                                                        : 0,
                                            }}
                                        />
                                        <Typography>Create</Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Chip
                                            label={modelMatching.name}
                                            size="small"
                                        />
                                    </Box>
                                </ListItemButton>
                            )}
                            {results.map((result) => {
                                const similarity =
                                    Math.round(result.score * 100).toString() +
                                    '%'
                                const isSelected =
                                    typeof modelMatching.selected !==
                                        'string' &&
                                    modelMatching.selected.obj.id ===
                                        result.obj.id
                                const onClick = () =>
                                    changeModelMatching(
                                        modelMatching.name,
                                        result,
                                    )

                                return (
                                    <ListItemButton
                                        selected={isSelected}
                                        sx={{ display: 'flex', width: '100%' }}
                                        key={result.obj.id}
                                        onClick={onClick}>
                                        <Box
                                            sx={{
                                                width: '4.4rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 0.5,
                                            }}>
                                            <Check
                                                sx={{
                                                    opacity: isSelected ? 1 : 0,
                                                }}
                                            />
                                            <Chip
                                                label={similarity}
                                                size="small"
                                                variant={
                                                    isSelected
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                color={
                                                    result.score >= 1
                                                        ? 'success'
                                                        : 'warning'
                                                }
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Chip
                                                label={result.obj.name}
                                                size="small"
                                            />
                                        </Box>
                                        <Box sx={{ width: '6rem' }}>
                                            {
                                                result.obj.product_manufacturer
                                                    ?.name
                                            }
                                        </Box>
                                        <Box sx={{ width: '6rem' }}>
                                            {result.obj.product_category?.name}
                                        </Box>
                                    </ListItemButton>
                                )
                            })}
                            {!hasPerfectMatch && (
                                <ListItemButton
                                    selected={
                                        modelMatching.selected === IGNORE_MODEL
                                    }
                                    sx={{ display: 'flex', width: '100%' }}
                                    onClick={onIgnoreClick}>
                                    <Box
                                        sx={{
                                            width: '4.4rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 0.5,
                                        }}>
                                        <Check
                                            sx={{
                                                opacity:
                                                    modelMatching.selected ===
                                                    IGNORE_MODEL
                                                        ? 1
                                                        : 0,
                                            }}
                                        />
                                        <Typography>Ignore</Typography>
                                    </Box>
                                </ListItemButton>
                            )}
                        </ListItem>
                        <Divider />
                    </Box>
                )
            })}
        </>
    )
}

const Summary = ({
    modelMatchings,
}: {
    modelMatchings: ProductModelMatching[]
}) => {
    const createNewModelsCount = useMemo(() => {
        return modelMatchings.filter(
            (modelMatching) => modelMatching.selected === CREATE_NEW_MODEL,
        ).length
    }, [modelMatchings])

    const ignoreModelsCount = useMemo(() => {
        return modelMatchings.filter(
            (modelMatching) => modelMatching.selected === IGNORE_MODEL,
        ).length
    }, [modelMatchings])

    const reuseModelsCount = useMemo(() => {
        return modelMatchings.filter(
            (modelMatching) =>
                modelMatching.selected !== CREATE_NEW_MODEL &&
                modelMatching.selected !== IGNORE_MODEL,
        ).length
    }, [modelMatchings])

    return (
        <Box
            sx={{
                px: 2,
                py: 2,
                background: (theme) => theme.palette.background.default,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                position: 'relative',
            }}>
            <Category sx={{ color: (theme) => theme.palette.text.primary }} />
            <Typography variant="body1" color="text.primary">
                Reuse {reuseModelsCount}{' '}
                {reuseModelsCount === 1 ? 'model' : 'models'}, create{' '}
                {createNewModelsCount} new{' '}
                {createNewModelsCount === 1 ? 'model' : 'models'}, and ignore{' '}
                {ignoreModelsCount}{' '}
                {ignoreModelsCount === 1 ? 'model' : 'models'}.
            </Typography>
        </Box>
    )
}
